import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Scale, Clock, Heart, Target } from 'lucide-react';
import { tracking } from '../../utils/tracking';

export function WorkLifeBalanceCoachPage() {
  const navigate = useNavigate();

  useEffect(() => {
    document.title = 'Work-Life Balance Coach - Find Balance & Set Boundaries | VZMO';

    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        'Improve work-life balance with AI coaching. Set boundaries, manage time, and prioritize what matters. Get personalized guidance 24/7. Start free.'
      );
    }

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', 'https://vzmo.ai/coach/work-life-balance');
    } else {
      const link = document.createElement('link');
      link.rel = 'canonical';
      link.href = 'https://vzmo.ai/coach/work-life-balance';
      document.head.appendChild(link);
    }

    window.scrollTo(0, 0);
  }, []);

  const handleStartFree = () => {
    tracking.startFreeClicked('work-life-balance-page');
    navigate('/signup');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-4xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6">
            Work-Life Balance Coach
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Find balance between career ambitions and personal life. Learn to set boundaries,
            manage time effectively, and prioritize what truly matters.
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8 sm:p-12 mb-12">
          <h2 className="text-2xl font-bold text-slate-900 mb-6">
            Build Sustainable Work-Life Balance
          </h2>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="flex items-start">
              <Scale className="w-6 h-6 text-[#E07A5F] mr-4 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-slate-900 mb-2">Define Your Priorities</h3>
                <p className="text-slate-600">
                  Clarify what balance means to you. Identify values and create a vision for how
                  you want to spend your time and energy.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <Clock className="w-6 h-6 text-[#E07A5F] mr-4 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-slate-900 mb-2">Set Boundaries</h3>
                <p className="text-slate-600">
                  Learn to say no, protect personal time, and communicate boundaries
                  effectively with colleagues and family.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <Target className="w-6 h-6 text-[#E07A5F] mr-4 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-slate-900 mb-2">Manage Your Time</h3>
                <p className="text-slate-600">
                  Develop systems for productivity, minimize distractions, and create
                  sustainable routines that support both work and life.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <Heart className="w-6 h-6 text-[#E07A5F] mr-4 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-slate-900 mb-2">Reduce Burnout</h3>
                <p className="text-slate-600">
                  Recognize early warning signs, implement recovery practices, and build
                  resilience for long-term wellbeing.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-8 mb-12">
          <div className="bg-white rounded-xl p-8 shadow-lg">
            <h3 className="text-xl font-bold text-slate-900 mb-4">Common Challenges</h3>
            <ul className="space-y-3 text-slate-600">
              <li className="flex items-start">
                <span className="text-[#E07A5F] mr-2">•</span>
                Always-on remote work culture
              </li>
              <li className="flex items-start">
                <span className="text-[#E07A5F] mr-2">•</span>
                Difficulty disconnecting from work
              </li>
              <li className="flex items-start">
                <span className="text-[#E07A5F] mr-2">•</span>
                Feeling guilty about personal time
              </li>
              <li className="flex items-start">
                <span className="text-[#E07A5F] mr-2">•</span>
                Neglecting relationships and health
              </li>
              <li className="flex items-start">
                <span className="text-[#E07A5F] mr-2">•</span>
                Burnout and exhaustion
              </li>
            </ul>
          </div>

          <div className="bg-white rounded-xl p-8 shadow-lg">
            <h3 className="text-xl font-bold text-slate-900 mb-4">What You'll Develop</h3>
            <ul className="space-y-3 text-slate-600">
              <li className="flex items-start">
                <span className="text-[#E07A5F] mr-2">•</span>
                Clear boundary-setting skills
              </li>
              <li className="flex items-start">
                <span className="text-[#E07A5F] mr-2">•</span>
                Effective time management
              </li>
              <li className="flex items-start">
                <span className="text-[#E07A5F] mr-2">•</span>
                Energy management strategies
              </li>
              <li className="flex items-start">
                <span className="text-[#E07A5F] mr-2">•</span>
                Self-care routines
              </li>
              <li className="flex items-start">
                <span className="text-[#E07A5F] mr-2">•</span>
                Guilt-free personal time
              </li>
            </ul>
          </div>
        </div>

        <div className="bg-gradient-to-r from-[#E07A5F] to-[#F4A261] rounded-2xl p-8 sm:p-12 text-white text-center mb-12">
          <Scale className="w-12 h-12 mx-auto mb-4" />
          <h2 className="text-3xl font-bold mb-4">Create Your Ideal Balance</h2>
          <p className="text-lg mb-6 opacity-90">
            Start building sustainable work-life balance. Try VZMO free for 7 days.
          </p>
          <button
            onClick={handleStartFree}
            className="bg-white text-[#E07A5F] px-8 py-4 rounded-lg font-semibold text-lg hover:bg-slate-50 transition-colors"
          >
            Start Free Trial
          </button>
        </div>

        <div className="bg-white rounded-xl p-8 shadow-lg">
          <h2 className="text-2xl font-bold text-slate-900 mb-6">Why AI Coaching for Balance?</h2>
          <div className="space-y-4 text-slate-600">
            <p>
              Work-life balance isn't one-size-fits-all. Your AI coach helps you define what balance
              means for you personally, considering your career stage, family situation, and personal values.
            </p>
            <p>
              Unlike generic advice, VZMO adapts to your unique situation. Whether you're a parent juggling
              childcare, a remote worker struggling with boundaries, or an ambitious professional seeking
              sustainable success, get personalized guidance that fits your life.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
